import React, { useState } from 'react';

const ConfigurationDialog = ({ onConfigComplete }) => {
  const [config, setConfig] = useState({
    npcType: '', // 'default', 'custom', 'not_sure'
    sceneTracking: '', // 'scores', 'scripts', 'not_sure'
    tutorial: '', // 'familiar', 'tutorial'
  });

  const [errors, setErrors] = useState({});

  const handleConfigChange = (field, value) => {
    setConfig(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user makes a selection
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const validateAndSubmit = () => {
    const newErrors = {};
    
    if (!config.npcType) {
      newErrors.npcType = 'Please select an NPC type option';
    }
    
    if (!config.sceneTracking) {
      newErrors.sceneTracking = 'Please select a scene tracking method';
    }
    
    if (!config.tutorial) {
      newErrors.tutorial = 'Please select your familiarity level';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    // All questions answered, proceed with configuration
    onConfigComplete(config);
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        padding: '40px',
        borderRadius: '12px',
        maxWidth: '600px',
        width: '90%',
        color: 'white',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)'
      }}>
        <h2 style={{ 
          marginTop: 0, 
          marginBottom: '30px', 
          textAlign: 'center',
          color: '#3498db',
          fontSize: '1.5rem'
        }}>
          NPC Dialogue Designer Configuration
        </h2>
        
        <p style={{ 
          marginBottom: '30px', 
          textAlign: 'center', 
          color: 'rgba(255, 255, 255, 0.8)',
          lineHeight: '1.4'
        }}>
          Please answer the following questions to configure the tool for your specific needs.
        </p>

        {/* Question 1: NPC Type */}
        <div style={{ marginBottom: '25px' }}>
          <h3 style={{ 
            marginBottom: '15px', 
            color: 'white',
            fontSize: '1.1rem'
          }}>
            1. Are you using default NPCs or custom entities?
          </h3>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {[
              { value: 'default', label: 'Default NPCs' },
              { value: 'custom', label: 'Custom entities' },
              { value: 'not_sure', label: 'Not sure' }
            ].map(option => (
              <label key={option.value} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                cursor: 'pointer',
                padding: '8px 12px',
                borderRadius: '6px',
                backgroundColor: config.npcType === option.value ? 'rgba(52, 152, 219, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                border: config.npcType === option.value ? '1px solid #3498db' : '1px solid rgba(255, 255, 255, 0.1)',
                transition: 'all 0.2s ease'
              }}>
                <input
                  type="radio"
                  name="npcType"
                  value={option.value}
                  checked={config.npcType === option.value}
                  onChange={(e) => handleConfigChange('npcType', e.target.value)}
                  style={{ width: '16px', height: '16px' }}
                />
                <span style={{ fontSize: '0.9rem' }}>{option.label}</span>
              </label>
            ))}
          </div>
          
          {errors.npcType && (
            <div style={{ color: '#e74c3c', fontSize: '0.8rem', marginTop: '5px' }}>
              {errors.npcType}
            </div>
          )}
        </div>

        {/* Question 2: Scene Tracking */}
        <div style={{ marginBottom: '25px' }}>
          <h3 style={{ 
            marginBottom: '15px', 
            color: 'white',
            fontSize: '1.1rem'
          }}>
            2. How are you tracking scene changes?
          </h3>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {[
              { value: 'scores', label: 'Scores' },
              { value: 'scripts', label: 'Scripts' },
              { value: 'not_sure', label: 'Not sure' }
            ].map(option => (
              <label key={option.value} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                cursor: 'pointer',
                padding: '8px 12px',
                borderRadius: '6px',
                backgroundColor: config.sceneTracking === option.value ? 'rgba(52, 152, 219, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                border: config.sceneTracking === option.value ? '1px solid #3498db' : '1px solid rgba(255, 255, 255, 0.1)',
                transition: 'all 0.2s ease'
              }}>
                <input
                  type="radio"
                  name="sceneTracking"
                  value={option.value}
                  checked={config.sceneTracking === option.value}
                  onChange={(e) => handleConfigChange('sceneTracking', e.target.value)}
                  style={{ width: '16px', height: '16px' }}
                />
                <span style={{ fontSize: '0.9rem' }}>{option.label}</span>
              </label>
            ))}
          </div>
          
          {errors.sceneTracking && (
            <div style={{ color: '#e74c3c', fontSize: '0.8rem', marginTop: '5px' }}>
              {errors.sceneTracking}
            </div>
          )}
        </div>

        {/* Question 3: Tutorial */}
        <div style={{ marginBottom: '30px' }}>
          <h3 style={{ 
            marginBottom: '15px', 
            color: 'white',
            fontSize: '1.1rem'
          }}>
            3. Are you familiar with the NPC Dialogue Designer?
          </h3>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {[
              { value: 'familiar', label: 'Yes, I\'m familiar with it' },
              { value: 'tutorial', label: 'No, I\'d like to go through a tutorial' }
            ].map(option => (
              <label key={option.value} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                cursor: 'pointer',
                padding: '8px 12px',
                borderRadius: '6px',
                backgroundColor: config.tutorial === option.value ? 'rgba(52, 152, 219, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                border: config.tutorial === option.value ? '1px solid #3498db' : '1px solid rgba(255, 255, 255, 0.1)',
                transition: 'all 0.2s ease'
              }}>
                <input
                  type="radio"
                  name="tutorial"
                  value={option.value}
                  checked={config.tutorial === option.value}
                  onChange={(e) => handleConfigChange('tutorial', e.target.value)}
                  style={{ width: '16px', height: '16px' }}
                />
                <span style={{ fontSize: '0.9rem' }}>{option.label}</span>
              </label>
            ))}
          </div>
          
          {errors.tutorial && (
            <div style={{ color: '#e74c3c', fontSize: '0.8rem', marginTop: '5px' }}>
              {errors.tutorial}
            </div>
          )}
        </div>

        {/* Submit Button */}
        <div style={{ textAlign: 'center' }}>
          <button
            onClick={validateAndSubmit}
            style={{
              backgroundColor: '#3498db',
              color: 'white',
              border: 'none',
              padding: '12px 30px',
              borderRadius: '6px',
              fontSize: '1rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              minWidth: '150px'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#2980b9'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#3498db'}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfigurationDialog;

// Enhanced NPCEditor component for custom entities
export const EnhancedNPCEditor = ({
  currentName,
  setCurrentName,
  npcNames,
  setNpcNames,
  selectedNpc,
  setSelectedNpc,
  dialogues,
  setDialogues,
  currentDialogue,
  setCurrentDialogue,
  selectedDialogue,
  setSelectedDialogue,
  currentButton,
  setCurrentButton,
  buttons,
  setButtons,
  editingDialogue,
  setEditingDialogue,
  config,
  // New props for custom entities
  currentEntityId,
  setCurrentEntityId,
  entityIds,
  setEntityIds
}) => {
  
  const isCustomEntity = config.npcType === 'custom';

  const handleInputChange = (event) => {
    setCurrentName(event.target.value);
  };

  const handleEntityIdChange = (event) => {
    setCurrentEntityId(event.target.value);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && currentName.trim() !== '') {
      if (isCustomEntity) {
        // For custom entities, we need both name and entity ID
        if (!currentEntityId || currentEntityId.trim() === '') {
          alert('Please enter both NPC name and Entity ID for custom entities.');
          return;
        }
        
        // Store the entity ID mapping
        setEntityIds(prev => ({
          ...prev,
          [currentName]: currentEntityId
        }));
      }
      
      setNpcNames(prev => [...prev, currentName]);
      setCurrentName('');
      setCurrentEntityId('');
    }
  };

  const handleEntityIdKeyDown = (event) => {
    if (event.key === 'Enter' && currentEntityId.trim() !== '' && currentName.trim() !== '') {
      // Store the entity ID mapping
      setEntityIds(prev => ({
        ...prev,
        [currentName]: currentEntityId
      }));
      
      setNpcNames(prev => [...prev, currentName]);
      setCurrentName('');
      setCurrentEntityId('');
    }
  };

  const handleNpcSelect = (npc) => {
    setSelectedNpc(npc || '');
  };

  const handleDialogueChange = (event) => {
    setCurrentDialogue(event.target.value);
  };

  const handleDialogueKeyDown = (event) => {
    if (event.key === 'Enter' && currentDialogue.trim() !== '') {
      if (editingDialogue) {
        setDialogues(prevDialogues => {
          let newDialogues = { ...prevDialogues };
          if (!newDialogues[editingDialogue.npc]) {
            newDialogues[editingDialogue.npc] = [];
          }
          newDialogues[editingDialogue.npc][editingDialogue.index] = currentDialogue;
          return newDialogues;
        });
        setEditingDialogue(null);
      } else if (selectedNpc) {
        setDialogues(prevDialogues => {
          const updatedDialogues = {
            ...prevDialogues,
            [selectedNpc]: [...(prevDialogues[selectedNpc] || []), currentDialogue]
          };
          
          setTimeout(() => {
            setSelectedDialogue(currentDialogue);
          }, 0);
          
          return updatedDialogues;
        });
      }
      setCurrentDialogue('');
    }
  };
  
  const handleDialogueSelect = (event) => {
    setSelectedDialogue(event.target.value || '');
  };
  
  const handleButtonChange = (event) => {
    setCurrentButton(event.target.value);
  };

  const handleButtonKeyDown = (event) => {
    if (event.key === 'Enter' && selectedNpc && selectedDialogue && currentButton.trim() !== '') {
      setButtons(prevButtons => {
        const newButtons = { ...prevButtons };
        if (!newButtons[selectedNpc]) {
          newButtons[selectedNpc] = {};
        }
        if (!newButtons[selectedNpc][selectedDialogue]) {
          newButtons[selectedNpc][selectedDialogue] = [];
        }
        
        const existingButtons = newButtons[selectedNpc][selectedDialogue];
        const buttonExists = existingButtons.includes(currentButton);
        
        if (!buttonExists) {
          const filteredButtons = existingButtons.filter(btn => btn !== '$placeholder_button');
          
          newButtons[selectedNpc][selectedDialogue] = [
            ...filteredButtons,
            currentButton
          ];
        }
        
        return newButtons;
      });
      setCurrentButton('');
    }
  };

  return (
    <div className="npc-editor-container">
      <div className="input-section">
        <div className="input-grid-form">
          <div className="input-group">
            <label htmlFor="npc-name-input">Enter NPC Name:</label>
            <input 
              id="npc-name-input"
              type="text" 
              value={currentName || ''}
              onChange={handleInputChange} 
              onKeyDown={handleKeyDown}
              placeholder="Press Enter to add" 
              className="app-input"
            />
          </div>
          
          {/* Show Entity ID field only for custom entities */}
          {isCustomEntity && (
            <div className="input-group">
              <label htmlFor="entity-id-input">Enter Entity ID:</label>
              <input 
                id="entity-id-input"
                type="text" 
                value={currentEntityId || ''}
                onChange={handleEntityIdChange} 
                onKeyDown={handleEntityIdKeyDown}
                placeholder="e.g., custom:my_npc" 
                className="app-input"
              />
            </div>
          )}
          
          <div className="input-group">
            <label htmlFor="npc-select">Select an NPC:</label>
            <select 
              id="npc-select"
              value={selectedNpc || ''} 
              onChange={(e) => handleNpcSelect(e.target.value)}
              className="app-select"
            >
              <option value="">-- Select an NPC --</option>
              {npcNames.map((name, index) => (
                <option key={index} value={name}>
                  {name} {isCustomEntity && entityIds[name] ? `(${entityIds[name]})` : ''}
                </option>
              ))}
            </select>
          </div>
          
          <div className="input-group">
            <label htmlFor="dialogue-input">Enter Dialogue:</label>
            <input 
              id="dialogue-input"
              type="text" 
              value={currentDialogue || ''}
              onChange={handleDialogueChange} 
              onKeyDown={handleDialogueKeyDown}
              placeholder={editingDialogue ? "Edit dialogue (press Enter to save)" : "Press Enter to add"}
              className="app-input"
              disabled={!selectedNpc}
            />
          </div>
          
          <div className="input-group">
            <label htmlFor="dialogue-select">Select a Dialogue:</label>
            <select 
              id="dialogue-select"
              onChange={handleDialogueSelect} 
              value={selectedDialogue || ''} 
              className="app-select"
              disabled={!selectedNpc || !dialogues[selectedNpc] || dialogues[selectedNpc].length === 0}
            >
              <option value="">-- Select Dialogue --</option>
              {((selectedNpc && dialogues[selectedNpc]) || []).map((dialogue, index) => (
                dialogue && <option key={index} value={dialogue}>{dialogue.substring(0, 40)}{dialogue.length > 40 ? '...' : ''}</option>
              ))}
            </select>
          </div>
          
          <div className="input-group">
            <label htmlFor="button-input">Enter Button Name:</label>
            <input 
              id="button-input"
              type="text" 
              value={currentButton || ''} 
              onChange={handleButtonChange} 
              onKeyDown={handleButtonKeyDown} 
              placeholder="Press Enter to add" 
              className="app-input"
              disabled={!selectedNpc || !selectedDialogue}
            />
          </div>
        </div>
      </div>
    </div>
  );
};